import { useState, useEffect, useCallback } from "react";
import { type Lang, defaultLang, supportedLangs, loadLocale, getDir, getNestedValue, getNestedArray } from "@/lib/i18n";

const STORAGE_KEY = "aurelian_lang";

export function useI18n() {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY) as Lang;
      if (stored && supportedLangs.includes(stored)) return stored;
    }
    return defaultLang;
  });

  const locale = loadLocale(lang);
  const dir = getDir(lang);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = useCallback((newLang: Lang) => {
    if (supportedLangs.includes(newLang)) {
      localStorage.setItem(STORAGE_KEY, newLang);
      setLangState(newLang);
    }
  }, []);

  const t = useCallback(
    (path: string): string => getNestedValue(locale, path),
    [locale]
  );

  const tArray = useCallback(
    (path: string): string[] => getNestedArray(locale, path),
    [locale]
  );

  return { lang, dir, t, tArray, setLang, loading: false };
}
